import { memo, useCallback } from 'react';

import { InfoCommentsSection } from '~/components/ShareModal/components/InfoCommentsSection/InfoCommentsSection';
import { OnOffShareToggleRow } from '~/components/ShareModal/components/OnOffShareToggleRow';
import { SecurityPrivacySection } from '~/components/ShareModal/components/SecurityPrivacySection/SecurityPrivacySection';
import { ShareModalSectionLabel } from '~/components/ShareModal/components/ShareModalSectionLabel';
import { AssetVersionControlRow } from '~/components/ShareModal/components/VersionsAndDownloadingSection/AssetVersionControlRow';
import { AssetVersionShareLinkSelector } from '~/components/SharePrivateClipModal/AssetVersionShareLinkSelector';
import {
  type AssetShareLinkOption,
  type AssetShareLinkSelectionContext,
} from '~/components/SharePrivateClipModal/buildAssetShareLinkSlots';
import { useAssetScopedShareLinkEditor } from '~/components/SharePrivateClipModal/hooks/useAssetScopedShareLinkEditor';
import { SharePrivateContentSpinner } from '~/components/SharePrivateContentModal/SharePrivateContentSpinner';
import { type PrivateAssetOpenLocation } from '~/hooks/usePrivateAssetOpenLocation';
import { type AssetScopedShareLinkListItem } from '~/swr-hooks/shortUrl/useListAssetShareLinks';
import {
  shareUrlUpdatedDownloadsFromClipToggle,
  shareUrlUpdatedVersionControl,
} from '~/utils/shortUrl/shareUrlUpdatedAnalytics';

export type SharePrivateAssetModalBodyProps = {
  assetId: string;
  assetShareLinksItems: AssetScopedShareLinkListItem[];
  analyticsLocation: PrivateAssetOpenLocation | null;
  canCreateShareLink: boolean;
  canViewCustomFields: boolean;
  canEditCustomFields: boolean;
  canViewDiscussions: boolean;
  canManageDiscussions: boolean;
  canViewAssetVersions: boolean;
  isFetchingShareLinkOptions?: boolean;
  onSelectedOptionChange: (optionKey: string) => void;
  options: AssetShareLinkOption[];
  selectedOption: AssetShareLinkOption | undefined;
  selectionContext: AssetShareLinkSelectionContext;
  workspaceCanDownload: boolean;
};

export const SharePrivateAssetModalBody = memo(
  ({
    assetId,
    assetShareLinksItems,
    analyticsLocation,
    canCreateShareLink,
    canViewCustomFields,
    canEditCustomFields,
    canViewDiscussions,
    canManageDiscussions,
    canViewAssetVersions,
    isFetchingShareLinkOptions = false,
    onSelectedOptionChange,
    options,
    selectedOption,
    selectionContext,
    workspaceCanDownload,
  }: SharePrivateAssetModalBodyProps) => {
    const {
      areSettingsDisabled,
      displayShortIdInfo,
      handleToggleEnablePublicLink,
      hasActiveShareLink,
      isEnableToggleLoading,
      isSettingsLoading,
      isShareLinkPublic,
      onShortUrlUpdate,
      selectedOptionKey,
    } = useAssetScopedShareLinkEditor({
      assetId,
      assetShareLinksItems,
      analyticsLocation,
      canCreateShareLink,
      canViewAssetVersions,
      selectedOption,
      selectionContext,
    });

    const handleToggleAllowDownloads = useCallback(
      (enabled: boolean) =>
        onShortUrlUpdate({
          props: { permissions: { asset: { canViewOriginal: enabled } } },
          analytics: shareUrlUpdatedDownloadsFromClipToggle(enabled),
        }),
      [onShortUrlUpdate],
    );

    const handleVersionControlChange = useCallback(
      (canViewAllVersions: boolean) =>
        onShortUrlUpdate({
          props: { permissions: { asset: { canViewAllVersions } } },
          analytics: shareUrlUpdatedVersionControl(canViewAllVersions, 'single-asset'),
        }),
      [onShortUrlUpdate],
    );

    const showSettingsSection = isSettingsLoading || displayShortIdInfo != null;

    return (
      <>
        <div className="-mx-6 -mt-4 flex flex-col gap-3 ads:bg-subtle bg-gray-3 px-6 py-4">
          <AssetVersionShareLinkSelector
            isFetchingShareLinkOptions={isFetchingShareLinkOptions}
            onSelect={onSelectedOptionChange}
            options={options}
            selectedOptionKey={selectedOptionKey}
          />
          <OnOffShareToggleRow
            label="Enable public link"
            disabled={!hasActiveShareLink && !canCreateShareLink}
            checked={isShareLinkPublic}
            isLoading={isEnableToggleLoading}
            onToggle={handleToggleEnablePublicLink}
            data-testid="ENABLE_PUBLIC_LINK_TOGGLE"
          />
        </div>
        <div
          aria-hidden
          className="sr-only"
          data-count={assetShareLinksItems.length}
          data-fetching-share-link-options={isFetchingShareLinkOptions}
          data-testid="ASSET_SHARE_LINKS_LIST_STATUS"
        />
        {showSettingsSection &&
          (isSettingsLoading && !displayShortIdInfo ? (
            <div className="flex flex-col gap-2">
              <ShareModalSectionLabel title="Versions & Downloading" />
              <SharePrivateContentSpinner />
            </div>
          ) : (
            displayShortIdInfo && (
              <>
                <div className="flex flex-col gap-2">
                  <ShareModalSectionLabel title="Versions & Downloading" />
                  <AssetVersionControlRow
                    variant="single-asset"
                    canViewAllVersions={displayShortIdInfo.permissions.asset.canViewAllVersions}
                    data-testid="ASSET_VERSION_CONTROL_SEGMENTED_CONTROL"
                    disabled={areSettingsDisabled}
                    isLoading={isSettingsLoading}
                    onChange={handleVersionControlChange}
                  />
                  <OnOffShareToggleRow
                    label="Allow downloads"
                    checked={displayShortIdInfo.permissions.asset.canViewOriginal}
                    // Asymmetric gate: turning OFF (restricting) is always allowed, but turning ON
                    // requires workspace-level download permission. Mirrors the 3-option row, where
                    // "None" stays selectable even when the workspace can't download — so a user
                    // whose workspace has lost download permission can still tighten an existing
                    // share link rather than being stuck.
                    disabled={
                      areSettingsDisabled ||
                      (!workspaceCanDownload && !displayShortIdInfo.permissions.asset.canViewOriginal)
                    }
                    isLoading={isSettingsLoading}
                    onToggle={handleToggleAllowDownloads}
                    data-testid="ALLOW_DOWNLOADS_TOGGLE"
                  />
                </div>
                <InfoCommentsSection
                  canEditCustomFields={canEditCustomFields}
                  canManageDiscussions={canManageDiscussions}
                  canViewCustomFields={canViewCustomFields}
                  canViewDiscussions={canViewDiscussions}
                  isLoading={isSettingsLoading}
                  settingsDisabled={areSettingsDisabled}
                  onShortUrlUpdate={onShortUrlUpdate}
                  shortIdInfo={displayShortIdInfo}
                />
                <SecurityPrivacySection
                  key={selectedOptionKey}
                  shortIdInfo={displayShortIdInfo}
                  isLoading={isSettingsLoading}
                  settingsDisabled={areSettingsDisabled}
                  onShortUrlUpdate={onShortUrlUpdate}
                />
              </>
            )
          ))}
      </>
    );
  },
);

SharePrivateAssetModalBody.displayName = 'SharePrivateAssetModalBody';
